<script lang="ts">
import { defineComponent, PropType } from 'vue'
import SolutionContentsCard from '@/components/solutions/SolutionContentsCard.vue'
import { DEFAULT_IMAGE_OPTIONS, DEFAULT_HEADING_OPTIONS } from '@/components/_parials/contentsCard'

export default defineComponent({
  name: 'LayoutSolutionContentsCards',

  components: {
    SolutionContentsCard,
  },

  props: {
    /**
     * 資料情報の配列
     */
    items: {
      type: Array as PropType<SolutionContentItem[]>,
      required: true,
    },

    /**
     * 表示する列数
     * 指定範囲: 1 〜 6
     * @description 'fixed' を指定した場合は、カラム一つひとつの幅を固定化する
     */
    cols: {
      type: Object as PropType<{
        pc: 1 | 2 | 3 | 4 | 5 | 6 | 'fixed',
        sp: 1 | 2 | 3 | 4 | 5 | 6 | 'fixed',
      }>,
      default: () => ({
        pc: 3,
        sp: 1,
      }),
      validator(value: { pc: number | string, sp: number | string }) {
        // 1 〜 6 の整数値が指定されてるかどうか
        const pc = value.pc === 'fixed' || (typeof value.pc === 'number' && [1,2,3,4,5,6].includes(value.pc))
        const sp = value.sp === 'fixed' || (typeof value.sp === 'number' && [1,2,3,4,5,6].includes(value.sp))

        return pc && sp
      },
    },

    /**
     * PC でも横向きタイプ表示にするフラグ
     * @description 重複定義の場合は full の設定が優先される
     */
    horizontal: {
      type: Object as PropType<{ pc: boolean, sp: boolean }>,
      default: () => ({
        pc: false,
        sp: true,
      }),
    },

    /**
     * ハイライト表示するワード
     */
    highlightWord: {
      type: String,
      default: '',
    },

    /**
     * 見出しのオプション
     */
    headingOptions: {
      type: Object as PropType<{
        // HTML 要素名
        tagName?: keyof HTMLElementTagNameMap,

        // CSS の line-height 値
        lineHeight?: number,

        // CSS の font-size
        fontSize?: {
          pc: string,
          sp: string,
        },

        // 文字の太さ
        fontWeight?: 'normal' | 'bold',

        // 表示最大行数
        maxLine?: number,
      }>,
      default: () => ({
        tagName: DEFAULT_HEADING_OPTIONS.TAG_NAME,
        lineHeight: DEFAULT_HEADING_OPTIONS.LINE_HEIGHT,
        fontSize: {
          pc: DEFAULT_HEADING_OPTIONS.FONT_SIZE.PC,
          sp: DEFAULT_HEADING_OPTIONS.FONT_SIZE.SP,
        },
        fontWeight: DEFAULT_HEADING_OPTIONS.FONT_WEIGHT,
        maxLine: DEFAULT_HEADING_OPTIONS.MAX_LINE,
      }),
    },

    /**
     * 画像の表示オプション
     * @description horizontal が有効のときのみ評価される
     */
    imageOptions: {
      type: Object as PropType<{
        // 幅
        width?: {
          pc: number,
          sp: number,
        },

        // 高さ
        height?: {
          pc: number,
          sp: number,
        },
      }>,
      default: () => ({
        width: {
          pc: DEFAULT_IMAGE_OPTIONS.WIDTH.PC,
          sp: DEFAULT_IMAGE_OPTIONS.WIDTH.SP,
        },
        height: {
          pc: DEFAULT_IMAGE_OPTIONS.HEIGHT.PC,
          sp: DEFAULT_IMAGE_OPTIONS.HEIGHT.SP,
        },
      }),
    },
  },
})
</script>

<template>
  <div
    v-if="items.length"
    class="l-cols__solutionContents"
    :data-cols-pc="cols.pc"
    :data-cols-sp="cols.sp"
    :style="{
      '--width-pc': imageOptions.width?.pc != null ? `${imageOptions.width?.pc}px` : '0',
      '--width-sp': imageOptions.width?.sp != null ? `${imageOptions.width?.sp}px` : '0',
    }">
    <div
      v-for="item in items"
      :key="item.id"
      class="l-cols__solutionContents--col">
      <SolutionContentsCard
        :item="item"
        :has-solution-link="false"
        :highlight-word="highlightWord"
        :horizontal="horizontal"
        :heading-options="headingOptions"
        :image-options="imageOptions" />
    <!-- /.l-cols__solutionContents col --></div>
  <!-- /.l-cols__solutionContents --></div>

  <p
    v-else
    class="p u-text__bold">
    データがありません
  </p>
</template>

<style lang="sass" scoped>
.l-cols__solutionContents
  $root: &
  --width-pc: 0
  --width-sp: 0
  display: flex
  flex-wrap: wrap
  margin: 1em auto 0

  &:first-child
    margin-top: 0

  // ===== Phablet 以上（PC） =====
  +breakpoint($max: false, $min: true)
    $horizontal-gap-px: 24
    $vertical-gap-px: 24

    // fixed 以外は縦マージンを付与
    &:not([data-cols-pc="fixed"])
      flex-wrap: wrap

      > #{$root}--col
        margin-top: changeSizePx2Em($vertical-gap-px)

    // fixed: カラム幅を固定し横スクロール
    &[data-cols-pc="fixed"]
      flex-wrap: nowrap
      overflow-x: auto
      gap: #{$horizontal-gap-px}px

      > #{$root}--col
        flex: 0 0 var(--width-pc)
        width: var(--width-pc)

    @for $i from 1 through 6
      &[data-cols-pc="#{$i}"]
        $col-width: math.div(($global-contents-width-pc - $horizontal-gap-px * ($i - 1)), $i)
        > #{$root}--col
          width: getRelativeValue($col-width, $global-contents-width-pc)
          margin-left: getRelativeValue($horizontal-gap-px, $global-contents-width-pc)

          &:nth-child(-n+#{$i})
            margin-top: 0

          &:nth-child(#{$i}n+1)
            margin-left: 0

  // ===== Phablet 未満（SP） =====
  +breakpoint
    $horizontal-gap-px: 12
    $vertical-gap-px: 12

    &:not([data-cols-sp="fixed"])
      flex-wrap: wrap

      > #{$root}--col
        margin-top: changeSizePx2Em($vertical-gap-px)

    &[data-cols-sp="fixed"]
      flex-wrap: nowrap
      overflow-x: auto
      gap: #{$horizontal-gap-px}px

      > #{$root}--col
        flex: 0 0 var(--width-sp)
        width: var(--width-sp)

    @for $i from 1 through 6
      &[data-cols-sp="#{$i}"]
        $col-width: math.div(($global-contents-width-phablet - $horizontal-gap-px * ($i - 1)), $i)
        > #{$root}--col
          width: getRelativeValue($col-width, $global-contents-width-phablet)
          margin-left: getRelativeValue($horizontal-gap-px, $global-contents-width-phablet)

          &:nth-child(-n+#{$i})
            margin-top: 0

          &:nth-child(#{$i}n+1)
            margin-left: 0

    // sp:1 のときは従来の見た目（カード下線 + 下 padding）を維持
    &[data-cols-sp="1"]
      > #{$root}--col
        padding-bottom: #{$vertical-gap-px}px
        border-bottom: 1px solid $border-x-light-gray

        &:last-child
          border-bottom: none
</style>
